import { readScheduleRows } from '../../realtime/useScheduleRealtime';
import { fetchBackgroundData } from '../../redux/api/portalApi';
import { panelRequest } from './panelSizingRequests';
import React, { useState, useEffect, useMemo } from 'react';
import useEngineeringCapacityRealtime from '../../realtime/useEngineeringCapacityRealtime';
import useSharedScheduleRealtime from '../../realtime/useSharedScheduleRealtime';
import useCalendarHolidaysRealtime from '../../realtime/useCalendarHolidaysRealtime';
import useGuardedRealtimeList from '../../realtime/useGuardedRealtimeList';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import TopNav from '../TopNav/TopNav';
import EngineeringCalculators from './EngineeringCalculators';
import TaskManagement from './TaskManagement';
import UserTasks from './UserTasks';
import EngineeringCalendar from './EngineeringCalendar';
import ProtectedTime from './ProtectedTime';
import MechanicalReview from './MechanicalReview';
import MechanicalReference from './MechanicalReference';
import AssignedEngineeringJobs from '../UserPage/AssignedEngineeringJobs';
import EngineerPTO from '../JobSchedulePage/EngineerPTO';
import { usePermissions } from '../../hooks/usePermissions';
import { fetchJobSchedule } from '../../redux/slices/jobScheduleSlice';
import { fetchTbdData } from '../../redux/slices/tbdApiSlice';
import { fetchAllChangeOrderDetails } from '../../redux/slices/changeOrderDetailsSlice';
import { setPtoEntries, selectPtoEntries } from '../../redux/slices/ptoSlice';
import { setRecords, selectProtectedTimeRecords } from '../../redux/slices/protectedTimeSlice';
import { selectApiUrls } from '../../redux/slices/apiUrlSlice';
import { selectUserDropdowns } from '../../redux/slices/dropdownSlice';
import { fetchData, patchData, postData, deleteData } from '../../redux/slices/apiCallSlice';
import { useEngineeringTasks } from '../../functions/useEngineeringTasks';
import useCriticalData from '../../functions/useCriticalData';
import { setTaskComments } from '../../redux/slices/engineeringTasksSlice';

import PanelSizingLog from './PanelSizingLog';
import UpcomingReview from './UpcomingReview';
import PanelSizingWIP from './PanelSizingWIP';
import PanelSizingChangeLog from './PanelSizingChangeLog';
import VTLossCalculator from './VTLossCalculator';
import OpenChangeOrderTasks from '../UserPage/OpenChangeOrderTasks';
import {
    getEngineeringPathForTab,
    getEngineeringTabFromPath,
} from '../../config/engineeringRoutes';

const buildPtoPayload = (ptoData, { recordId = null, requestCase = 'pascal' } = {}) => {
    const engineerId = ptoData?.EngineerId ?? ptoData?.engineerId ?? '';
    const userName = ptoData?.UserName ?? ptoData?.userName ?? '';
    const startDate = ptoData?.StartDate ?? ptoData?.startDate ?? '';
    const endDate = ptoData?.EndDate ?? ptoData?.endDate ?? '';
    const notes = ptoData?.Notes ?? ptoData?.notes ?? '';

    if (requestCase === 'camel') {
        const payload = {
            userName,
            startDate,
            endDate,
            notes,
        };

        if (engineerId) {
            payload.engineerId = engineerId;
        }

        return payload;
    }

    const payload = {
        UserName: userName,
        StartDate: startDate,
        EndDate: endDate,
        Notes: notes,
    };

    if (engineerId) {
        payload.EngineerId = engineerId;
    }

    if (recordId !== undefined && recordId !== null) {
        payload.ID = recordId;
    }

    return payload;
};

const buildMechanicalReviewScheduleUrl = (fullScheduleUrl) => {
    if (!fullScheduleUrl) return null;

    const queryJoiner = fullScheduleUrl.includes('?') ? '&' : '?';
    return `${fullScheduleUrl}${queryJoiner}page=1&projectStatusNotEqual=${encodeURIComponent('Shipped')}`;
};

const extractGeneralProjectInfoFromSchedule = (scheduleResponse) => {
    const rawData = scheduleResponse?.data || scheduleResponse || {};

    if (Array.isArray(rawData)) {
        return rawData.map((item) => item?.GeneralProjectInfo || item).filter(Boolean);
    }

    return Object.values(rawData)
        .map((item) => item?.GeneralProjectInfo || item)
        .filter(Boolean);
};

const EngineeringContainer = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const [calculatorSubTab, setCalculatorSubTab] = useState('general');
    const [panelSizingSubTab, setPanelSizingSubTab] = useState('upcomingReview'); // Sub-tab state for Panel Sizing tab
    const { canView, isLoading: isPermissionsLoading } = usePermissions();
    const { fetchTasks: fetchEngineeringTasks, tasks: engineeringTasks } = useEngineeringTasks();
    
    // Load critical data including dropdownValues, globalVariables, hoverDescriptions, and user dropdowns
    // Force refresh to ensure we have the latest data for engineering workflows
    useCriticalData({
        apiKeys: ['dropdownValues', 'globalVariables', 'hoverDescriptions'],
        includeUserDropdowns: true,
        autoLoad: true,
        forceRefresh: false
    });
    
    // Permission-based visibility
    const canAccessEngineeringTools = canView('home.engineering-tools');
    const canViewCalculators = canAccessEngineeringTools && canView('engineering.calculators');
    const canViewEngineeringCalendar = canAccessEngineeringTools && canView('engineering.calendar');
    const canViewAssignedJobs = canAccessEngineeringTools && canView('engineering.assigned-jobs');
    const canViewEngineerPto = canAccessEngineeringTools && canView('engineering.pto-management');
    const canViewTaskManagement = canAccessEngineeringTools && canView('engineering.task-management');
    const canViewUserTasks = canAccessEngineeringTools && canView('engineering.user-tasks');
    const canViewProtectedTime = canAccessEngineeringTools && canView('engineering.protected-time');
    const canViewMechanicalReview = canAccessEngineeringTools && canView('engineering.mechanical-review');
    const canViewMechanicalReference = canAccessEngineeringTools && canView('engineering.mechanical-reference');
    const canViewPanelSizing = canAccessEngineeringTools && canView('engineering.panel-sizing');
    const canViewChangeOrderTasks = canAccessEngineeringTools && canView('engineering.change-order-tasks');

    const availableTabs = useMemo(() => ([
        canViewCalculators ? 'calculators' : null,
        canViewEngineeringCalendar ? 'engineeringCalendar' : null,
        canViewAssignedJobs ? 'assignedJobs' : null,
        canViewEngineerPto ? 'engineerPto' : null,
        canViewTaskManagement ? 'taskManagement' : null,
        canViewUserTasks ? 'myTasks' : null,
        canViewProtectedTime ? 'protectedTime' : null,
        canViewMechanicalReview ? 'mechanicalReview' : null,
        canViewMechanicalReference ? 'mechanicalReference' : null,
        canViewPanelSizing ? 'panelSizing' : null,
        canViewChangeOrderTasks ? 'changeOrderTasks' : null,
    ].filter(Boolean)), [
        canViewCalculators,
        canViewEngineeringCalendar,
        canViewAssignedJobs,
        canViewEngineerPto,
        canViewTaskManagement,
        canViewUserTasks,
        canViewProtectedTime,
        canViewMechanicalReview,
        canViewMechanicalReference,
        canViewPanelSizing,
        canViewChangeOrderTasks,
    ]);

    const requestedTab = getEngineeringTabFromPath(location.pathname);
    const activeTab = requestedTab && availableTabs.includes(requestedTab)
        ? requestedTab
        : availableTabs[0] || 'calculators';

    useEffect(() => {
        if (
            isPermissionsLoading ||
            !canAccessEngineeringTools ||
            availableTabs.length === 0
        ) {
            return;
        }

        const canonicalPath = getEngineeringPathForTab(activeTab);
        const currentPath = location.pathname.replace(/\/+$/, '').toLowerCase();
        if (currentPath !== canonicalPath.toLowerCase()) {
            navigate(canonicalPath, { replace: true });
        }
    }, [
        activeTab,
        availableTabs.length,
        canAccessEngineeringTools,
        isPermissionsLoading,
        location.pathname,
        navigate,
    ]);

    const handleTabChange = (tabKey) => {
        navigate(getEngineeringPathForTab(tabKey));
    };
    
    // Selectors
    // Note: Dropdown options and critical data are now loaded via useCriticalData hook
    const jobScheduleData = useSelector((state) => state.jobSchedule.data);
    const jobScheduleLoading = useSelector((state) => state.jobSchedule.loading);
    useSharedScheduleRealtime({ enabled: canViewEngineeringCalendar || canViewAssignedJobs, blocked: jobScheduleLoading || isPermissionsLoading });
    useCalendarHolidaysRealtime(canViewEngineeringCalendar);
    const ptoEntries = useSelector(selectPtoEntries);
    const protectedTimeRecords = useSelector(selectProtectedTimeRecords);
    const apiUrls = useSelector(selectApiUrls);
    useEngineeringCapacityRealtime({ pto: canViewEngineerPto || canViewEngineeringCalendar, protectedTime: canViewProtectedTime || canViewEngineeringCalendar });
    const userDropdowns = useSelector(selectUserDropdowns);

    // Local state for general project data (for Mechanical Review)
    const [generalProjectData, setGeneralProjectData] = useState([]);
    const [generalProjectLoading, setGeneralProjectLoading] = useState(false);
    const [generalProjectError, setGeneralProjectError] = useState(null);

    // Local state for Panel Sizing tab data (shared between Log and Possible Review)
    const [panelSizingGeneralProjectData, setPanelSizingGeneralProjectData] = useState([]);
    const [panelSizingElectricalOptionsData, setPanelSizingElectricalOptionsData] = useState({});
    const [panelSizingWaterTreatmentData, setPanelSizingWaterTreatmentData] = useState({});
    const [panelSizingGeneralStationData, setPanelSizingGeneralStationData] = useState({});
    const [panelSizingFertigationData, setPanelSizingFertigationData] = useState({});
    const [panelSizingPumpsData, setPanelSizingPumpsData] = useState({});
    const [panelSizingPanelSizingData, setPanelSizingPanelSizingData] = useState({});
    const [panelSizingDataLoading, setPanelSizingDataLoading] = useState(false);
    const [panelSizingDataError, setPanelSizingDataError] = useState(null);
    const panelRealtimeEnabled = activeTab === 'panelSizing' && canViewPanelSizing;
    const panelMap = rows => Object.fromEntries(rows.filter(row => row.ProjectID != null).map(row => [row.ProjectID, row]));
    useGuardedRealtimeList({ apiKey: 'fullSchedule', enabled: activeTab === 'mechanicalReview' && canViewMechanicalReview,
        params: { page: 1, projectStatusNotEqual: 'Shipped' },
        decode: payload => readScheduleRows(payload).rows, apply: setGeneralProjectData });
    useGuardedRealtimeList({ apiKey: 'generalProject', enabled: panelRealtimeEnabled, blocked: panelSizingDataLoading, apply: setPanelSizingGeneralProjectData });
    useGuardedRealtimeList({ apiKey: 'electricalOptions', enabled: panelRealtimeEnabled, blocked: panelSizingDataLoading, apply: rows => setPanelSizingElectricalOptionsData(panelMap(rows)) });
    useGuardedRealtimeList({ apiKey: 'waterTreatment', enabled: panelRealtimeEnabled, blocked: panelSizingDataLoading, apply: rows => setPanelSizingWaterTreatmentData(panelMap(rows)) });
    useGuardedRealtimeList({ apiKey: 'generalStation', enabled: panelRealtimeEnabled, blocked: panelSizingDataLoading, apply: rows => setPanelSizingGeneralStationData(panelMap(rows)) });
    useGuardedRealtimeList({ apiKey: 'fertigation', enabled: panelRealtimeEnabled, blocked: panelSizingDataLoading, apply: rows => setPanelSizingFertigationData(panelMap(rows)) });
    useGuardedRealtimeList({ apiKey: 'pumps', enabled: panelRealtimeEnabled, blocked: panelSizingDataLoading, apply: rows => setPanelSizingPumpsData(panelMap(rows)) });
    useGuardedRealtimeList({ apiKey: 'panelSizing', enabled: panelRealtimeEnabled, blocked: panelSizingDataLoading, apply: rows => setPanelSizingPanelSizingData(panelMap(rows)) });

    // Debug logging for data state
    useEffect(() => {
        console.log('EngineeringContainer - Data State:', {
            jobScheduleData: Array.isArray(jobScheduleData) ? jobScheduleData.length : 'not an array',
            engineeringTasks: Array.isArray(engineeringTasks) ? engineeringTasks.length : 'not an array',
            jobScheduleLoading,
            apiUrlsAvailable: !!apiUrls.generalProject,
            ptoEntries: ptoEntries.length,
            protectedTimeRecords: protectedTimeRecords.length
        });
    }, [jobScheduleData, engineeringTasks, jobScheduleLoading, apiUrls, ptoEntries, protectedTimeRecords]);

    // Fetch job schedule data on mount - match JobScheduleContainer pattern
    useEffect(() => {
        if (!canAccessEngineeringTools || (!canViewEngineeringCalendar && !canViewAssignedJobs)) {
            return;
        }

        console.log('EngineeringContainer: Fetching job schedule data on mount');
        dispatch(fetchJobSchedule())
            .unwrap()
            .then((data) => {
                console.log('EngineeringContainer: Job schedule fetched successfully', data?.length || 0, 'jobs');
            })
            .catch((error) => {
                console.error('EngineeringContainer: Error fetching job schedule:', error);
            });
    }, [dispatch, canAccessEngineeringTools, canViewEngineeringCalendar, canViewAssignedJobs]);

    // Fetch TBD data on mount - needed for AssignedEngineeringJobs notice icons
    useEffect(() => {
        if (!canViewAssignedJobs) {
            return;
        }

        console.log('EngineeringContainer: Fetching TBD data');
        dispatch(fetchTbdData()).catch((error) => {
            console.error('EngineeringContainer: Error fetching TBD data:', error);
        });
    }, [dispatch, canViewAssignedJobs]);

    // Fetch change order details on mount - needed for AssignedEngineeringJobs notice icons
    useEffect(() => {
        if (!canViewAssignedJobs && !canViewChangeOrderTasks) {
            return;
        }

        console.log('EngineeringContainer: Fetching change order details');
        dispatch(fetchAllChangeOrderDetails()).catch((error) => {
            console.error('EngineeringContainer: Error fetching change order details:', error);
        });
    }, [dispatch, canViewAssignedJobs, canViewChangeOrderTasks]);

    // Fetch PTO entries on mount - match JobScheduleContainer pattern
    useEffect(() => {
        if (canViewEngineerPto && apiUrls.engineeringPto) {
            console.log('EngineeringContainer: Fetching PTO entries');
            dispatch(fetchData({ url: apiUrls.engineeringPto }))
                .unwrap()
                .then((data) => {
                    console.log('EngineeringContainer: PTO entries fetched', data?.length || 0);
                    dispatch(setPtoEntries(data));
                })
                .catch((error) => {
                    console.error('EngineeringContainer: Error fetching PTO entries:', error);
                });
        }
    }, [dispatch, apiUrls.engineeringPto, canViewEngineerPto]);

    // Fetch Protected Time records on mount - match JobScheduleContainer pattern
    useEffect(() => {
        if (canViewProtectedTime && apiUrls.engineeringProtectedTime) {
            console.log('EngineeringContainer: Fetching Protected Time records');
            dispatch(fetchData({ url: apiUrls.engineeringProtectedTime }))
                .unwrap()
                .then((data) => {
                    console.log('EngineeringContainer: Protected Time records fetched', data?.length || 0);
                    dispatch(setRecords(data));
                })
                .catch((error) => {
                    console.error('EngineeringContainer: Error fetching Protected Time records:', error);
                });
        }
    }, [dispatch, apiUrls.engineeringProtectedTime, canViewProtectedTime]);

    // Fetch Engineering Tasks on mount
    useEffect(() => {
        if (!canViewTaskManagement && !canViewUserTasks) {
            return;
        }

        console.log('EngineeringContainer: Fetching engineering tasks');
        fetchEngineeringTasks()
            .then(() => {
                console.log('EngineeringContainer: Engineering tasks fetched successfully');
            })
            .catch((error) => {
                console.error('EngineeringContainer: Error fetching engineering tasks:', error);
            });
    }, [fetchEngineeringTasks, canViewTaskManagement, canViewUserTasks]);

    // Prefetch all engineering task comments on mount to warm comment state
    useEffect(() => {
        if ((canViewTaskManagement || canViewUserTasks) && apiUrls.engineeringTaskComments) {
            console.log('EngineeringContainer: Prefetching engineering task comments');
            dispatch(fetchData({ url: apiUrls.engineeringTaskComments }))
                .unwrap()
                .then((data) => {
                    const groupedComments = (data || []).reduce((acc, comment) => {
                        const taskId = comment.TaskID ?? comment.taskID ?? comment.taskId;
                        if (!taskId) return acc;
                        if (!acc[taskId]) {
                            acc[taskId] = [];
                        }
                        acc[taskId].push(comment);
                        return acc;
                    }, {});

                    Object.entries(groupedComments).forEach(([taskId, comments]) => {
                        dispatch(setTaskComments({ taskId, comments }));
                    });

                    console.log('EngineeringContainer: Engineering task comments prefetched', data?.length || 0);
                })
                .catch((error) => {
                    console.error('EngineeringContainer: Error prefetching engineering task comments:', error);
                });
        }
    }, [dispatch, apiUrls.engineeringTaskComments, canViewTaskManagement, canViewUserTasks]);

    // Fetch scheduled jobs for Mechanical Review tab
    useEffect(() => {
        const mechanicalReviewScheduleUrl = buildMechanicalReviewScheduleUrl(apiUrls.fullSchedule);

        if (activeTab === 'mechanicalReview' && canViewMechanicalReview && mechanicalReviewScheduleUrl) {
            console.log('EngineeringContainer: Fetching scheduled jobs for Mechanical Review');
            setGeneralProjectLoading(true);
            setGeneralProjectError(null);
            
            dispatch(fetchData({ url: mechanicalReviewScheduleUrl }))
                .unwrap()
                .then((data) => {
                    const scheduledJobs = extractGeneralProjectInfoFromSchedule(data);
                    console.log('EngineeringContainer: Mechanical Review schedule data fetched', scheduledJobs.length || 0, 'jobs');
                    setGeneralProjectData(scheduledJobs);
                })
                .catch((error) => {
                    console.error('EngineeringContainer: Error fetching Mechanical Review schedule data:', error);
                    setGeneralProjectError('Failed to load job data. Please try again.');
                })
                .finally(() => {
                    setGeneralProjectLoading(false);
                });
        }
    }, [dispatch, apiUrls.fullSchedule, activeTab, canViewMechanicalReview]);

    // Fetch Panel Sizing data when Panel Sizing tab is active (shared data for Log and Possible Review)
    useEffect(() => {
        let canceled = false;
        if (activeTab === 'panelSizing' && canViewPanelSizing && apiUrls.generalProject && panelSizingGeneralProjectData.length === 0) {
            console.log('EngineeringContainer: Fetching Panel Sizing data (shared for Log and Possible Review)');
            setPanelSizingDataLoading(true);
            setPanelSizingDataError(null);
            
            const fetchPanelSizingData = async () => {
                try {
                    // Fetch all data from base URLs (no projectID filtering)
                    const [
                        generalProjectResponse,
                        electricalOptionsResponse,
                        waterTreatmentResponse,
                        generalStationResponse,
                        fertigationResponse,
                        pumpsResponse,
                        panelSizingResponse
                    ] = await Promise.all([
                        panelRequest(dispatch, 'GET', apiUrls.generalProject).then(data => ({ data })),
                        apiUrls.electricalOptions ? panelRequest(dispatch, 'GET', apiUrls.electricalOptions).then(data => ({ data })) : { data: [] },
                        apiUrls.waterTreatment ? panelRequest(dispatch, 'GET', apiUrls.waterTreatment).then(data => ({ data })) : { data: [] },
                        apiUrls.generalStation ? panelRequest(dispatch, 'GET', apiUrls.generalStation).then(data => ({ data })) : { data: [] },
                        apiUrls.fertigation ? panelRequest(dispatch, 'GET', apiUrls.fertigation).then(data => ({ data })) : { data: [] },
                        apiUrls.pumps ? panelRequest(dispatch, 'GET', apiUrls.pumps).then(data => ({ data })) : { data: [] },
                        apiUrls.panelSizing ? panelRequest(dispatch, 'GET', apiUrls.panelSizing).then(data => ({ data })) : { data: [] }
                    ]);
                    
                    console.log('EngineeringContainer: Panel Sizing data fetched', generalProjectResponse.data?.length || 0, 'jobs');
                    
                    if (canceled) return;
                    // Store General Project data
                    setPanelSizingGeneralProjectData(generalProjectResponse.data || []);
                    
                    // Convert child table arrays to maps keyed by ProjectID for easy lookup
                    const elecOptionsMap = {};
                    (electricalOptionsResponse.data || []).forEach(item => {
                        if (item.ProjectID) {
                            elecOptionsMap[item.ProjectID] = item;
                        }
                    });
                    setPanelSizingElectricalOptionsData(elecOptionsMap);
                    
                    const waterTreatmentMap = {};
                    (waterTreatmentResponse.data || []).forEach(item => {
                        if (item.ProjectID) {
                            waterTreatmentMap[item.ProjectID] = item;
                        }
                    });
                    setPanelSizingWaterTreatmentData(waterTreatmentMap);
                    
                    const generalStationMap = {};
                    (generalStationResponse.data || []).forEach(item => {
                        if (item.ProjectID) {
                            generalStationMap[item.ProjectID] = item;
                        }
                    });
                    setPanelSizingGeneralStationData(generalStationMap);
                    
                    const fertigationMap = {};
                    (fertigationResponse.data || []).forEach(item => {
                        if (item.ProjectID) {
                            fertigationMap[item.ProjectID] = item;
                        }
                    });
                    setPanelSizingFertigationData(fertigationMap);
                    
                    const pumpsMap = {};
                    (pumpsResponse.data || []).forEach(item => {
                        if (item.ProjectID) {
                            pumpsMap[item.ProjectID] = item;
                        }
                    });
                    setPanelSizingPumpsData(pumpsMap);
                    
                    const panelSizingMap = {};
                    (panelSizingResponse.data || []).forEach(item => {
                        if (item.ProjectID) {
                            panelSizingMap[item.ProjectID] = item;
                        }
                    });
                    setPanelSizingPanelSizingData(panelSizingMap);
                    
                } catch (error) {
                    console.error('EngineeringContainer: Error fetching Panel Sizing data:', error);
                    if (!canceled) setPanelSizingDataError('Failed to load data. Please try again.');
                } finally {
                    if (!canceled) setPanelSizingDataLoading(false);
                }
            };
            
            fetchPanelSizingData();
        }
        return () => { canceled = true; };
    }, [dispatch, apiUrls, activeTab, panelSizingGeneralProjectData.length, canViewPanelSizing]);

    // Handle job completion for Mechanical Review
    const handleJobCompletion = async (jobId, updateData) => {
        if (!apiUrls.generalProject) {
            throw new Error('General Project API URL not available');
        }
        
        try {
            await dispatch(patchData({ 
                url: `${apiUrls.generalProject}?id=${jobId}`, 
                data: updateData 
            })).unwrap();
            
            // Refresh the Mechanical Review list from the schedule source.
            const mechanicalReviewScheduleUrl = buildMechanicalReviewScheduleUrl(apiUrls.fullSchedule);
            if (mechanicalReviewScheduleUrl) {
                const refreshedData = await dispatch(fetchData({ url: mechanicalReviewScheduleUrl })).unwrap();
                setGeneralProjectData(extractGeneralProjectInfoFromSchedule(refreshedData));
            }
            
            return true;
        } catch (error) {
            console.error('EngineeringContainer: Error updating job:', error);
            throw error;
        }
    };

    // PTO Management Handlers
    const handleCreatePto = async (ptoData) => {
        try {
            await dispatch(postData({ 
                url: apiUrls.engineeringPto, 
                data: buildPtoPayload(ptoData, { requestCase: 'pascal' })
            })).unwrap();
            
            // Refresh PTO entries
            const data = await dispatch(fetchData({ url: apiUrls.engineeringPto })).unwrap();
            dispatch(setPtoEntries(data));
        } catch (error) {
            console.error('Error creating PTO entry:', error);
            throw error;
        }
    };

    const handleUpdatePto = async (ptoData) => {
        try {
            const entryId = ptoData?.id ?? ptoData?.ID;

            await dispatch(patchData({ 
                url: `${apiUrls.engineeringPto}?id=${entryId}`,
                data: buildPtoPayload(ptoData, { recordId: entryId, requestCase: 'pascal' })
            })).unwrap();
            
            // Refresh PTO entries
            const data = await dispatch(fetchData({ url: apiUrls.engineeringPto })).unwrap();
            dispatch(setPtoEntries(data));
        } catch (error) {
            console.error('Error updating PTO entry:', error);
            throw error;
        }
    };

    const handleDeletePto = async (id) => {
        try {
            await dispatch(deleteData({ 
                url: `${apiUrls.engineeringPto}?id=${id}` 
            })).unwrap();
            
            // Refresh PTO entries
            const data = await dispatch(fetchData({ url: apiUrls.engineeringPto })).unwrap();
            dispatch(setPtoEntries(data));
        } catch (error) {
            console.error('Error deleting PTO entry:', error);
            throw error;
        }
    };

    const handleRefreshUserDropdowns = () => {
        console.log('EngineeringContainer: Refreshing user dropdowns via critical data');
        // User dropdowns are now managed by useCriticalData hook
        // The hook auto-loads and caches data, so this is a no-op
        // If refresh is needed, the hook will handle it automatically
    };

    // Manual refresh handler for testing
    const handleRefreshData = () => {
        console.log('EngineeringContainer: Manual data refresh triggered');
        dispatch(fetchJobSchedule())
            .unwrap()
            .then((data) => {
                console.log('EngineeringContainer: Manual refresh - Job schedule fetched', data?.length || 0, 'jobs');
            })
            .catch((error) => {
                console.error('EngineeringContainer: Manual refresh - Error fetching job schedule:', error);
            });
    };

    // Panel Sizing data refresh handler
    const handlePanelSizingDataRefresh = async () => {
        if (!apiUrls.panelSizing) return;
        
        try {
            // Refetch panel sizing data
            const panelSizingData = await dispatch(fetchBackgroundData({ url: apiUrls.panelSizing }));
            if (!Array.isArray(panelSizingData)) throw new Error('Invalid panel sizing refresh response');
            
            const panelSizingMap = {};
            (panelSizingData || []).forEach(item => {
                if (item.ProjectID) {
                    panelSizingMap[item.ProjectID] = item;
                }
            });
            setPanelSizingPanelSizingData(panelSizingMap);
            
            console.log('EngineeringContainer: Panel sizing data refreshed');
        } catch (error) {
            console.error('EngineeringContainer: Error refreshing panel sizing data:', error);
            throw error;
        }
    };

    // Save notification message handler (placeholder - could integrate with toast system)
    const showSaveNotificationMessage = (message, variant = 'success') => {
        console.log(`[${variant.toUpperCase()}] ${message}`);
        // TODO: Integrate with toast notification system if available
        // For now, using browser alert for visibility
        if (variant === 'danger') {
            alert(`Error: ${message}`);
        }
    };

    const renderTabContent = () => {
        switch(activeTab) {
            case 'calculators':
                return (
                    <div className="calculator-container">
                        <div className="calculator-sub-tabs">
                            <button 
                                className={`calculator-sub-tab ${calculatorSubTab === 'general' ? 'active' : ''}`}
                                onClick={() => setCalculatorSubTab('general')}
                            >
                                <i className="bi bi-calculator me-2"></i>
                                General Calculators
                            </button>
                            <button 
                                className={`calculator-sub-tab ${calculatorSubTab === 'vtLoss' ? 'active' : ''}`}
                                onClick={() => setCalculatorSubTab('vtLoss')}
                            >
                                <i className="bi bi-bar-chart-line me-2"></i>
                                VT Loss Calculator
                            </button>
                        </div>
                        <div className="calculator-sub-tab-content">
                            {calculatorSubTab === 'general' && <EngineeringCalculators />}
                            {calculatorSubTab === 'vtLoss' && <VTLossCalculator />}
                        </div>
                    </div>
                );
            case 'engineeringCalendar':
                return <EngineeringCalendar />;
            case 'assignedJobs':
                return (
                    <AssignedEngineeringJobs 
                        jobs={jobScheduleData || []}
                        loading={jobScheduleLoading}
                        error={null}
                        onJobCompletion={handleJobCompletion}
                        userDropdowns={userDropdowns}
                    />
                );
            case 'engineerPto':
                return (
                    <EngineerPTO 
                        ptoEntries={ptoEntries}
                        loading={false}
                        error={null}
                        onCreatePto={handleCreatePto}
                        onUpdatePto={handleUpdatePto}
                        onDeletePto={handleDeletePto}
                        userDropdowns={userDropdowns}
                        onRefreshUserDropdowns={handleRefreshUserDropdowns}
                    />
                );
            case 'taskManagement':
                return <TaskManagement />;
            case 'myTasks':
                return <UserTasks />;
            case 'protectedTime':
                return <ProtectedTime />;
            case 'mechanicalReview':
                return (
                    <MechanicalReview 
                        jobs={generalProjectData}
                        loading={generalProjectLoading}
                        error={generalProjectError}
                        onJobCompletion={handleJobCompletion}
                    />
                );
            case 'mechanicalReference':
                return <MechanicalReference />;
            case 'panelSizing':
                return (
                    <div className="panel-sizing-container">
                        {/* Panel Sizing Sub-tabs */}
                        <div className="panel-sizing-sub-tabs">
                            <button
                                className={`panel-sizing-sub-tab ${panelSizingSubTab === 'upcomingReview' ? 'active' : ''}`}
                                onClick={() => setPanelSizingSubTab('upcomingReview')}
                            >
                                <i className="bi bi-clipboard-check me-2"></i>
                                Upcoming Review
                            </button>
                            {canViewPanelSizing && (
                                <button
                                    className={`panel-sizing-sub-tab ${panelSizingSubTab === 'wip' ? 'active' : ''}`}
                                    onClick={() => setPanelSizingSubTab('wip')}
                                >
                                    <i className="bi bi-tools me-2"></i>
                                    WIP
                                </button>
                            )}
                            {canViewPanelSizing && (
                                <button
                                    className={`panel-sizing-sub-tab ${panelSizingSubTab === 'log' ? 'active' : ''}`}
                                    onClick={() => setPanelSizingSubTab('log')}
                                >
                                    <i className="bi bi-check-circle me-2"></i>
                                    Log
                                </button>
                            )}
                            {canViewPanelSizing && (
                                <button
                                    className={`panel-sizing-sub-tab ${panelSizingSubTab === 'changeLog' ? 'active' : ''}`}
                                    onClick={() => setPanelSizingSubTab('changeLog')}
                                >
                                    <i className="bi bi-clock-history me-2"></i>
                                    Change Log
                                </button>
                            )}
                        </div>
                        
                        {/* Panel Sizing Sub-tab Content - Shared data prevents refetching */}
                        <div className="panel-sizing-sub-tab-content">
                            {panelSizingSubTab === 'upcomingReview' && (
                                <UpcomingReview
                                    generalProjectData={panelSizingGeneralProjectData}
                                    panelSizingData={panelSizingPanelSizingData}
                                    loading={panelSizingDataLoading}
                                    error={panelSizingDataError}
                                    onDataUpdate={handlePanelSizingDataRefresh}
                                />
                            )}
                            {panelSizingSubTab === 'wip' && canViewPanelSizing && (
                                <PanelSizingWIP
                                    generalProjectData={panelSizingGeneralProjectData}
                                    panelSizingData={panelSizingPanelSizingData}
                                    loading={panelSizingDataLoading}
                                    error={panelSizingDataError}
                                    onDataUpdate={handlePanelSizingDataRefresh}
                                />
                            )}
                            {panelSizingSubTab === 'log' && canViewPanelSizing && (
                                <PanelSizingLog
                                    generalProjectData={panelSizingGeneralProjectData}
                                    electricalOptionsData={panelSizingElectricalOptionsData}
                                    waterTreatmentData={panelSizingWaterTreatmentData}
                                    generalStationData={panelSizingGeneralStationData}
                                    fertigationData={panelSizingFertigationData}
                                    pumpsData={panelSizingPumpsData}
                                    panelSizingData={panelSizingPanelSizingData}
                                    loading={panelSizingDataLoading}
                                    error={panelSizingDataError}
                                    showSaveNotificationMessage={showSaveNotificationMessage}
                                    onDataUpdate={handlePanelSizingDataRefresh}
                                />
                            )}
                            {panelSizingSubTab === 'changeLog' && canViewPanelSizing && (
                                <PanelSizingChangeLog />
                            )}
                        </div>
                    </div>
                );
            case 'changeOrderTasks':
                return <OpenChangeOrderTasks />;
            default:
                return <EngineeringCalculators />;
        }
    };

    return (
        <div>
            <TopNav />
            {isPermissionsLoading ? (
                <div className="text-center p-4">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : !canAccessEngineeringTools ? (
                <div className="alert alert-danger m-3">Access denied. Engineering tools are not available for this account.</div>
            ) : (
            <div className="engineering-container">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h1>Engineering Portal</h1>
                    {activeTab === 'engineeringCalendar' && canViewEngineeringCalendar && (
                        <button 
                            className="btn btn-sm btn-outline-primary"
                            onClick={handleRefreshData}
                            disabled={jobScheduleLoading}
                        >
                            <i className="bi bi-arrow-clockwise me-2"></i>
                            {jobScheduleLoading ? 'Loading...' : 'Refresh Job Data'}
                        </button>
                    )}
                </div>
                
                <div className="tabs">
                    {/* Calculators - Visible to Admin, Engineer, EngineerManager */}
                    {canViewCalculators && (
                        <button 
                            className={`tab ${activeTab === 'calculators' ? 'active' : ''}`}
                            onClick={() => handleTabChange('calculators')}
                        >
                            <i className="bi bi-calculator me-2"></i>
                            Calculators
                        </button>
                    )}
                    
                    {/* Calendar - Visible to Admin, Engineer, EngineerManager */}
                    {canViewEngineeringCalendar && (
                        <button 
                            className={`tab ${activeTab === 'engineeringCalendar' ? 'active' : ''}`}
                            onClick={() => handleTabChange('engineeringCalendar')}
                        >
                            <i className="bi bi-calendar-week me-2"></i>
                            Calendar
                        </button>
                    )}
                    
                    {/* Assigned Jobs - Visible to Admin, Engineer, EngineerManager */}
                    {canViewAssignedJobs && (
                        <button 
                            className={`tab ${activeTab === 'assignedJobs' ? 'active' : ''}`}
                            onClick={() => handleTabChange('assignedJobs')}
                        >
                            <i className="bi bi-clipboard-check me-2"></i>
                            Assigned Jobs
                        </button>
                    )}
                    
                    {/* PTO Management - Visible to Admin, Engineer, EngineerManager */}
                    {canViewEngineerPto && (
                        <button 
                            className={`tab ${activeTab === 'engineerPto' ? 'active' : ''}`}
                            onClick={() => handleTabChange('engineerPto')}
                        >
                            <i className="bi bi-calendar-x me-2"></i>
                            PTO Management
                        </button>
                    )}
                    
                    {/* Task Management - Visible to Admin, EngineerManager */}
                    {canViewTaskManagement && (
                        <button 
                            className={`tab ${activeTab === 'taskManagement' ? 'active' : ''}`}
                            onClick={() => handleTabChange('taskManagement')}
                        >
                            <i className="bi bi-list-task me-2"></i>
                            Task Management
                        </button>
                    )}
                    
                    {/* My Tasks - Visible to Admin, Engineer, EngineerManager */}
                    {canViewUserTasks && (
                        <button 
                            className={`tab ${activeTab === 'myTasks' ? 'active' : ''}`}
                            onClick={() => handleTabChange('myTasks')}
                        >
                            <i className="bi bi-person-check me-2"></i>
                            My Tasks
                        </button>
                    )}
                    
                    {/* Protected Time - Visible to Admin, EngineerManager */}
                    {canViewProtectedTime && (
                        <button 
                            className={`tab ${activeTab === 'protectedTime' ? 'active' : ''}`}
                            onClick={() => handleTabChange('protectedTime')}
                        >
                            <i className="bi bi-shield-lock me-2"></i>
                            Protected Time
                        </button>
                    )}
                    
                    {/* Mechanical Review - Visible to Admin, Engineer, EngineerManager */}
                    {canViewMechanicalReview && (
                        <button 
                            className={`tab ${activeTab === 'mechanicalReview' ? 'active' : ''}`}
                            onClick={() => handleTabChange('mechanicalReview')}
                        >
                            <i className="bi bi-check-square me-2"></i>
                            Mechanical Review
                        </button>
                    )}
                    
                    {/* Mechanical Reference - Admin Only */}
                    {canViewMechanicalReference && (
                        <button 
                            className={`tab ${activeTab === 'mechanicalReference' ? 'active' : ''}`}
                            onClick={() => handleTabChange('mechanicalReference')}
                        >
                            <i className="bi bi-book me-2"></i>
                            Mechanical Reference
                        </button>
                    )}
                    
                    {/* Panel Sizing - Admin Only */}
                    {canViewPanelSizing && (
                        <button 
                            className={`tab ${activeTab === 'panelSizing' ? 'active' : ''}`}
                            onClick={() => handleTabChange('panelSizing')}
                        >
                            <i className="bi bi-table me-2"></i>
                            Panel Sizing
                        </button>
                    )}
                    
                    {/* Change Order Tasks - Visible to Admin, Engineer, EngineerManager */}
                    {canViewChangeOrderTasks && (
                        <button 
                            className={`tab ${activeTab === 'changeOrderTasks' ? 'active' : ''}`}
                            onClick={() => handleTabChange('changeOrderTasks')}
                        >
                            <i className="bi bi-clipboard-check me-2"></i>
                            Change Order Tasks
                        </button>
                    )}
                </div>
                
                <div className="tab-content">
                    {availableTabs.length > 0
                        ? renderTabContent()
                        : <div className="alert alert-warning">No engineering tabs are currently available.</div>}
                </div>
            </div>
            )}

            <style jsx>{`
                .engineering-container {
                    padding: 20px;
                    max-width: 100%;
                    width: 100%;
                    margin: 0;
                }
                
                .tabs {
                    display: flex;
                    flex-wrap: wrap;
                    border-bottom: 1px solid #dee2e6;
                    margin-bottom: 20px;
                    background-color: #f8f9fa;
                    border-radius: 0.375rem 0.375rem 0 0;
                }
                
                .tab {
                    padding: 12px 20px;
                    background: none;
                    border: none;
                    border-bottom: 3px solid transparent;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 500;
                    color: #6c757d;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                    white-space: nowrap;
                }
                
                .tab:hover {
                    background-color: #e9ecef;
                    color: #0066cc;
                }
                
                .tab.active {
                    border-bottom: 3px solid #0066cc;
                    font-weight: 600;
                    color: #0066cc;
                    background-color: #fff;
                }
                
                .tab-content {
                    padding: 20px 0;
                }

                .card {
                    border: 1px solid #dee2e6;
                    border-radius: 0.375rem;
                    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
                    transition: box-shadow 0.15s ease-in-out;
                }

                .card:hover {
                    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
                }

                .card-body {
                    padding: 1.5rem;
                }

                .bi {
                    font-size: 1rem;
                }
                
                .panel-sizing-container {
                    width: 100%;
                }
                
                .panel-sizing-sub-tabs {
                    display: flex;
                    border-bottom: 2px solid #dee2e6;
                    margin-bottom: 20px;
                    background-color: #f8f9fa;
                    border-radius: 0.375rem 0.375rem 0 0;
                    padding: 0 10px;
                }
                
                .panel-sizing-sub-tab {
                    padding: 10px 18px;
                    background: none;
                    border: none;
                    border-bottom: 3px solid transparent;
                    cursor: pointer;
                    font-size: 13px;
                    font-weight: 500;
                    color: #6c757d;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                    white-space: nowrap;
                }
                
                .panel-sizing-sub-tab:hover {
                    background-color: #e9ecef;
                    color: #0066cc;
                }
                
                .panel-sizing-sub-tab.active {
                    border-bottom: 3px solid #0066cc;
                    font-weight: 600;
                    color: #0066cc;
                    background-color: #fff;
                }
                
                .panel-sizing-sub-tab-content {
                    padding: 0;
                }
                
                .calculator-container {
                    width: 100%;
                }
                
                .calculator-sub-tabs {
                    display: flex;
                    border-bottom: 2px solid #dee2e6;
                    margin-bottom: 20px;
                    background-color: #f8f9fa;
                    border-radius: 0.375rem 0.375rem 0 0;
                    padding: 0 10px;
                }
                
                .calculator-sub-tab {
                    padding: 10px 18px;
                    background: none;
                    border: none;
                    border-bottom: 3px solid transparent;
                    cursor: pointer;
                    font-size: 13px;
                    font-weight: 500;
                    color: #6c757d;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                    white-space: nowrap;
                }
                
                .calculator-sub-tab:hover {
                    background-color: #e9ecef;
                    color: #0066cc;
                }
                
                .calculator-sub-tab.active {
                    border-bottom: 3px solid #0066cc;
                    font-weight: 600;
                    color: #0066cc;
                    background-color: #fff;
                }
                
                .calculator-sub-tab-content {
                    padding: 0;
                }
            `}</style>
        </div>
    );
};

export default EngineeringContainer;
